import { ApiService } from "./api.service"
import { StorageService } from "./storage.service"
import { SyncService } from "./sync-v2/reliable-sync-service"
import { StripeService } from "./stripe.service"
import { OffersService } from "./offers.service"
import { TvActivationService } from "./tv-activation.service"
import { router } from "@/router"
import { VersionService } from "./version.service"
import { TrackingService } from "./tracking.service"
import { DeviceService } from "./device.service"

export class ServiceContainer {
  private static instance: ServiceContainer | null = null
  private services = new Map<string, any>()

  static getInstance(): ServiceContainer {
    if (!this.instance) {
      this.instance = new ServiceContainer()
    }
    return this.instance
  }

  async get<T>(name: 'api'): Promise<ApiService>
  async get<T>(name: 'storage'): Promise<StorageService>
  async get<T>(name: 'sync'): Promise<SyncService>
  async get<T>(name: 'stripe'): Promise<StripeService>
  async get<T>(name: 'offers'): Promise<OffersService>
  async get<T>(name: 'tv-activation'): Promise<TvActivationService>
  async get<T>(name: 'version'): Promise<VersionService>
  async get<T>(name: 'tracking'): Promise<TrackingService>
  async get<T>(name: 'device'): Promise<DeviceService>
  async get<T>(name: string): Promise<T> {
    if (!this.services.has(name)) {
      switch (name) {
        case 'device': {
          const storageService = await this.get('storage')
          const deviceService = new DeviceService(storageService)
          // Initialize device ID on first access
          await deviceService.initialize()
          this.services.set(name, deviceService)
          break
        }
        case 'api': {
          const { ApiService } = await import('./api.service')
          const apiService = new ApiService()
          // Set device service on API service
          const deviceService = await this.get('device')
          apiService.setDeviceService(deviceService)
          this.services.set(name, apiService)
          break
        }
        case 'storage':
          const { StorageService } = await import('./storage.service')
          this.services.set(name, new StorageService())
          break
        case 'sync': {
          const { SyncService } = await import('./sync-v2/reliable-sync-service')
          const apiService = await this.get('api')
          const storageService = await this.get('storage')
          const deviceService = await this.get('device')
          const syncService = new SyncService(apiService, storageService, deviceService)
          await syncService.initialize()
          this.services.set(name, syncService)
          break
        }
        case 'stripe': {
          const { StripeService } = await import('./stripe.service')
          const apiService = await this.get('api')
          const storageService = await this.get('storage')
          this.services.set(name, new StripeService(apiService, storageService))
          break
        }
        case 'offers': {
          const { OffersService } = await import('./offers.service')
          const apiService = await this.get('api')
          this.services.set(name, new OffersService(apiService))
          break
        }
        case 'tv-activation': {
          const { TvActivationService } = await import('./tv-activation.service')
          const apiService = await this.get('api')
          this.services.set(name, new TvActivationService(apiService))
          break
        }
        case 'version':
          this.services.set(name, new VersionService())
          break
        case 'tracking': {
          const { TrackingService } = await import('./tracking.service')
          const apiService = await this.get('api')
          this.services.set(name, new TrackingService(apiService))
          break
        }
      }
    }
    return this.services.get(name)
  }
} 